// Import highlight.js styles for code highlighting
import "highlight.js/styles/github.css"

import {
    Alert,
    Box,
    Center,
    HStack,
    Link,
    Spinner,
    Text,
    VStack
} from "@chakra-ui/react"
import { useColorModeValue } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { memo, useMemo } from "react"
import DiscussionUnlocksOverlay from "../DiscussionLockedOverlay"
import { useDiscussionContext } from "../DiscussionProvider"
import DiscussionItemContainer from "../list/item/DiscussionItemContainer"
import { DiscussionResponseFauxForm } from "./DiscussionResponseForm"
import DiscussionResponsesOrderBySelect from "./DiscussionResponsesOrderBySelect"
import ResponseList from "./ResponseList"
import useDiscussionResponsePermissions from "./useDiscussionResponsePermissions"

const DiscussionPanel = memo(() => {
  const { t } = useTranslate()

  const post = useDiscussionContext((c) => c.post)
  const context = useDiscussionContext((c) => c.context)
  const postLoading = useDiscussionContext((c) => c.postLoading)
  const responsesLoading = useDiscussionContext((c) => c.responsesLoading)
  const responsesPaginator = useDiscussionContext((c) => c.responsesPaginator)
  const responses = useDiscussionContext((c) => c.responses)
  const permissions = useDiscussionResponsePermissions(post?.cohort_id!)

  // Get the intent from the url search params, not using useQueryParam as we don't wish
  // to trigger a re-render when the intent changes (it's only used for rendering the initial state).
  const intent = useMemo(() => {
    const url = new URL(window.location.href)
    return url.searchParams.get("intent")
  }, [])

  const showNewResponsesPrompt = false

  const bgColor = useColorModeValue("gray.50", "gray.800")

  return (
    <VStack
      className="post-detail-view chakra-ui-reset-css"
      bg={bgColor}
      minH="100%"
      align="stretch"
      pos="relative"
    >
      {postLoading && !responsesPaginator.loadingMore ? (
        <Center py={8} flexGrow={1}>
          <Spinner size="md" color="gray.500" />
        </Center>
      ) : post ? (
        <VStack gap={4} align="stretch">
          {/* Discussion post */}
          <DiscussionItemContainer
            initialMode={
              intent === "reply"
                ? "view"
                : intent === "update"
                ? "edit"
                : post
                ? "view"
                : "create"
            }
            discussion={post}
            context={context}
            compact={false}
            css={{
              ["&.DiscussionItemContainer__edit, &.DiscussionItemContainer__create"]:
                {
                  /* Enforces a min margin of 10px on left and right sides */
                  mW: "min(700px, 100%, calc(100% - 20px))",
                  m: "20px auto",
                  bg: "white",
                  borderRadius: "card"
                },
              ["& .DiscussionListItem"]: {
                borderRadius: 0,
                boxShadow: "none"
              },
              ["& .DiscussionForm"]: {
                // Avatar can get cut off in narrow containers.
                // maxW: "unset",
                // w: "100%"
              }
            }}
          />

          <VStack gap={4} p={4} align="stretch" pos="relative">
            {/* Response form region (top) */}
            {permissions.canCreate && <DiscussionResponseFauxForm />}

            {!!responses.length && (
              <HStack justify="flex-end">
                <DiscussionResponsesOrderBySelect />
              </HStack>
            )}

            {/* New responses available prompt */}
            {showNewResponsesPrompt && (
              <Alert.Root status="info" className="new-responses-prompt">
                <Alert.Indicator>
                  <Pathicon icon="info-circle" />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Title>{t("post.detail.new_responses")}</Alert.Title>
                  <Alert.Description>
                    <Link
                      id="load-new-responses"
                      href="javascript:;"
                      onClick={(e) => {
                        e.preventDefault()
                        // reloadResponses()
                      }}
                      color="blue.600"
                      fontWeight="semibold"
                    >
                      {t("post.detail.load_responses")}.
                    </Link>
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {/* Response/s */}
            <ResponseList />

            {responsesLoading && (
              <Box pos="absolute" inset={0} bg="whiteAlpha.600" />
            )}
          </VStack>
        </VStack>
      ) : (
        <Center py={8} flexGrow={1}>
          <Text fontSize="lg" color="gray.500">
            {t("discussion.not_found")}
          </Text>
        </Center>
      )}

      {!!post && (
        <DiscussionUnlocksOverlay
          cohortId={post.cohort_id!}
          pathItemSourceId={post.step_id!}
        />
      )}
    </VStack>
  )
})

export default DiscussionPanel
